import React, { useEffect, useMemo, useState } from 'react';
import { p, tp, useP } from '../../i18n/useP';
import { useTranslation } from 'react-i18next';
import { listHrContracts, listHrPeople, peopleCostSynthesis, peopleCostSynthesisYtd } from '../../api/hr';
import { formatNumber, formatMoney } from '../../utils/format';
import { monthlyMassSeries, extraMonths } from './salaryMass';
import { MONTHS, buildPeopleView, monthlyTotals } from './peopleMonthly';
import PageHeader from '../../components/PageHeader';
import { usePersistentValue } from '../../hooks/usePersistentState';
import './HrPage.css';

// Masse mensuelle (base + salaires extra positionnés sur leur mois de versement).
function baseMonthlyFromContracts(contracts, year, months = [12, 6]) {
  return monthlyMassSeries(contracts, year, months);
}

/* ── Page component ──────────────────────────────────────────────────────── */

export default function HrYearEndSimulationPage({ activeTenant, year: yearProp, embedded, unit = 'chf' }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const year = yearProp ?? new Date().getFullYear();  // année globale (topbar) — cohérent avec le print
  const m = (v, d = 0) => formatMoney(v, unit, d);    // montants selon l'unité (CHF/kCHF)
  const chfLbl = unit === 'kchf' ? 'kCHF' : 'CHF';    // suffixe d'unité

  const [contracts, setContracts] = useState([]);
  const [people, setPeople] = useState([]);
  const [synth, setSynth] = useState(null);
  const [synthPrev, setSynthPrev] = useState(null);   // atterrissage N-1 (comparaison)
  const [ytd, setYtd] = useState(null);
  const [loading, setLoading] = useState(false);

  function refresh() {
    if (!tenantId) return;
    setLoading(true);
    Promise.all([
      listHrContracts(tenantId),
      listHrPeople(tenantId),
      peopleCostSynthesis(tenantId, year, 'all'),
      peopleCostSynthesisYtd(tenantId, year, 'all'),
      peopleCostSynthesis(tenantId, year - 1, 'all'),
    ])
      .then(([c, p, s, y, sp]) => {
        if (!c.error) setContracts(c.data || []);
        if (!p.error) setPeople(p.data || []);
        if (!s.error) setSynth(s.data || s);
        if (y && !y.error) setYtd(y.data || y);
        setSynthPrev(sp && !sp.error ? (sp.data || sp) : null);
      })
      .finally(() => setLoading(false));
  }

  useEffect(refresh, [tenantId, year]); // eslint-disable-line

  // Baseline annuelle dérivée uniquement des contrats (entrées/sorties déjà
  // connues via start_date / end_date des contrats).
  const base = useMemo(() => baseMonthlyFromContracts(contracts, year, extraMonths(activeTenant)), [contracts, year, activeTenant?.thirteenth_salary_month, activeTenant?.thirteenth_salary_month_2, activeTenant?.fourteenth_salary_month_13, activeTenant?.fourteenth_salary_month]);

  // Profil mensuel du salaire brut = SOURCE UNIQUE backend (synthèse atterrissage,
  // `monthly_gross` : base + 13e/14e + compléments, Σ = total_effective_remuneration).
  // Aucun recalcul du brut côté front. Repli sur la base contrats le temps du chargement.
  const grossMonthly = useMemo(() => {
    const mg = synth?.monthly_gross;
    return (Array.isArray(mg) && mg.length === 12) ? mg.map(Number) : base;
  }, [synth, base]);

  // Toggle 13e/14e (même principe que Planification personnel) : Vue cash =
  // grossMonthly (source backend, pic sur le mois de versement) ; 13e lissé =
  // même total annuel, réparti à parts égales sur les mois actifs des contrats
  // (pure transformation d'affichage — le total réel `grossAnnual` ne change
  // pas, seule sa répartition mensuelle est recalculée puis mise à l'échelle).
  const hasExtraSalary = useMemo(() => contracts.some(c => Number(c.nb_of_salary || 12) > 12), [contracts]);
  const [smoothExtras, setSmoothExtras] = usePersistentValue(`hr_synthesis_smooth13_${tenantId}`, false);
  const smoothedGrossMonthly = useMemo(() => {
    const annualized = monthlyMassSeries(contracts, year, extraMonths(activeTenant), true);
    const annualizedTotal = annualized.reduce((a, n) => a + n, 0);
    const grossAnnualNow = (Array.isArray(synth?.monthly_gross) ? synth.monthly_gross : base).reduce((a, n) => a + Number(n || 0), 0);
    if (!annualizedTotal) return annualized;
    const factor = grossAnnualNow / annualizedTotal;
    return annualized.map(v => v * factor);
  }, [contracts, year, activeTenant, synth, base]);
  const displayMonthly = smoothExtras ? smoothedGrossMonthly : grossMonthly;

  // Moyenne mensuelle et Δ fin/début restent sur la base contractuelle (`base`).
  // Le total brut et le profil mensuel viennent de la synthèse (source unique).
  const annual    = base.reduce((a, n) => a + n, 0);
  const grossAnnual = grossMonthly.reduce((a, n) => a + n, 0);
  const avgMonthly = annual / 12;

  // ── Cockpit : à date (cumul YTD) → atterrissage (année pleine, même moteur) ──
  // À date = people-synthesis-ytd ; atterrissage = people-synthesis pleine année
  // (synth). Même fonction → lignes cohérentes (frais + LPP inclus, réconcilie).
  const ytdSynth   = ytd?.synthesis || null;
  const ytdCap     = ytd?.capacity || null;
  const ytdTurn    = ytd?.turnover || null;
  const ytdLabel   = ytd?.period_label || p('à date');
  const ytdNoPeriod = ytd && !ytd.through_month;
  const num = (o, k) => (o ? Number(o[k] || 0) : 0);
  // À date
  const ytdGross   = num(ytdSynth, 'total_effective_remuneration');
  const ytdCost    = num(ytdSynth, 'total_cost_empr');
  const ytdSocEmpr = num(ytdSynth, 'total_social_empr');
  const ytdFrais   = num(ytdSynth, 'total_other_fees');
  const ytdNet     = num(ytdSynth, 'total_estimated_net');
  const ytdLpp     = num(ytdSynth, 'total_lpp_empr') + num(ytdSynth, 'total_lpp_empl');
  // Coût/EPT annualisé : le coût « à date » est cumulé sur N mois clos, on le
  // ramène à l'année (×12/N) avant de le diviser par l'EPT — sinon on mélange un
  // cumul partiel avec un effectif, et le chiffre n'est comparable à rien.
  const ytdMonths = ytd?.through_month || 0;
  const ytdCostPerFte = ytdCap && ytdCap.ept_moyen && ytdMonths
    ? (ytdCost / ytdMonths) * 12 / ytdCap.ept_moyen
    : null;
  // Atterrissage (année pleine)
  const yrGross    = num(synth, 'total_effective_remuneration');
  const yrCost     = num(synth, 'total_cost_empr');
  const prevCost   = num(synthPrev, 'total_cost_empr');   // atterrissage N-1 (0 si absent)
  const prevDeltaPct = prevCost ? ((yrCost - prevCost) / prevCost) * 100 : null;
  const yrSocEmpr  = num(synth, 'total_social_empr');
  const yrFrais    = num(synth, 'total_other_fees');
  const yrNet      = num(synth, 'total_estimated_net');
  const yrLpp      = num(synth, 'total_lpp_empr') + num(synth, 'total_lpp_empl');
  const barPct     = yrCost ? Math.min(100, (ytdCost / yrCost) * 100) : 0;
  // Taux de charges sociales employeur (LPP comprise) = Σ CS empr ÷ Σ brut.
  // Résultat agrégé (total/total), pas un taux moyen appliqué avant la somme.
  const ytdSocEmprRate = ytdGross ? (ytdSocEmpr / ytdGross) * 100 : 0;
  const yrSocEmprRate   = yrGross ? (yrSocEmpr / yrGross) * 100 : 0;

  // Mois min/max actifs (utile pour repérer une rampe de recrutement
  // déjà signée ou un départ planifié).
  const firstActiveIdx = base.findIndex(v => v > 0);
  const lastActiveIdx  = base.length - 1 - [...base].reverse().findIndex(v => v > 0);
  const rampDelta = (firstActiveIdx >= 0 && lastActiveIdx >= 0)
    ? base[lastActiveIdx] - base[firstActiveIdx]
    : 0;

  const maxBar = Math.max(...displayMonthly, 1);

  // Frontière réel / projeté : nombre de mois clos dans `year`, dérivé de
  // tenant.last_closed_period (jour=1). Les mois au-delà sont projetés → barres
  // hachurées. Aucun mois clos (ou année future) ⇒ toute l'année est projetée.
  const closedThrough = useMemo(() => {
    const lcp = activeTenant?.last_closed_period ? new Date(activeTenant.last_closed_period) : null;
    if (!lcp || Number.isNaN(lcp.getTime())) return 0;
    if (lcp.getFullYear() > year) return 12;
    if (lcp.getFullYear() < year) return 0;
    return lcp.getMonth() + 1;
  }, [activeTenant?.last_closed_period, year]);

  // FTE moyen annuel (cockpit) — même moteur que l'onglet Effectifs (sur tout
  // l'effectif, sans filtre département) pour ne jamais diverger.
  const avgFte = useMemo(
    () => monthlyTotals(buildPeopleView(people, contracts, year)).avgFte,
    [people, contracts, year],
  );

  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div></main>;

  return (
    <main className={`hr-page hr-overview${embedded ? ' is-embedded' : ''}`}>
      {!embedded && (
        <PageHeader
          title={t('phrases:p_0e1c8edd17')}
        />
      )}

      {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
      {!loading && (
        <div className="hr-ov-stack">

          {/* ── Card 1 — Masse salariale (cockpit : à date → atterrissage) ── */}
          <div className="hr-charges-card">
            <div className="hr-charges-card__band">MASSE SALARIALE · à date → atterrissage {year}</div>
            <div className="hr-charges-card__body">
              {ytdNoPeriod ? (
                <div className="hr-synth__note">ℹ {p('Aucun mois clos pour {{year}}.', { year })}</div>
              ) : (
                <>
                  <div className="hr-cockpit-hero">
                    <div>
                      <div className="hr-cockpit-lbl">{p('Coût total employeur · à date')}</div>
                      <div className="hr-cockpit-hero__big">{m(ytdCost)}<small className="hr-kpi__unit"> {chfLbl}</small></div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="hr-cockpit-lbl">Atterrissage {year}</div>
                      <div className="hr-cockpit-hero__land">{m(yrCost)}<small className="hr-kpi__unit"> {chfLbl}</small></div>
                      {prevCost > 0 && (
                        <div className="hr-cockpit-hero__cmp">
                          {year - 1} : {m(prevCost)} {chfLbl}
                          {prevDeltaPct != null && (
                            <span style={{ color: prevDeltaPct >= 0 ? 'var(--color-sage)' : 'var(--color-terracotta)' }}>
                              {' · '}{prevDeltaPct >= 0 ? '+' : ''}{formatNumber(prevDeltaPct, 1)} %
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="hr-cockpit-bar"><div style={{ width: `${barPct}%` }} /></div>

                  {/* KPI toujours affichés (le repli « + Plus d'informations »
                      est retiré le 2026-08-21 : un clic pour lire cinq chiffres
                      qu'on vient précisément chercher ici). */}
                  <div className="hr-cockpit-grid">
                    <KpiCell label={p('Salaires bruts')}             value={m(ytdGross)}   suffix={chfLbl} hint={`→ ${m(yrGross)} fin ${year}`} />
                    <KpiCell label={p('Charges sociales employeur')} value={m(ytdSocEmpr)} suffix={chfLbl} hint={`${formatNumber(ytdSocEmprRate, 1)} % du brut · → ${m(yrSocEmpr)} (${formatNumber(yrSocEmprRate, 1)} %)`} />
                    <KpiCell label={t('phrases:p_665714163b')}           value={m(ytdLpp)}     suffix={chfLbl} hint={`→ ${m(yrLpp)}`} />
                    <KpiCell label={p('Frais de personnel')}         value={m(ytdFrais)}   suffix={chfLbl} hint={`→ ${m(yrFrais)}`} />
                    <KpiCell label={p('Salaire net')}                value={m(ytdNet)}     suffix={chfLbl} hint={`→ ${m(yrNet)}`} />
                  </div>

                  <div className="hr-cockpit-proj">
                    <div>
                      <div className="hr-cockpit-proj__lbl">{p('Moyenne mensuelle')}</div>
                      <div className="hr-cockpit-proj__val">{m(avgMonthly)}<small className="hr-kpi__unit"> {chfLbl}</small></div>
                    </div>
                    <div>
                      <div className="hr-cockpit-proj__lbl">{p('FTE moyen annuel')}</div>
                      <div className="hr-cockpit-proj__val">{formatNumber(avgFte, 1)}</div>
                    </div>
                    <div>
                      <div className="hr-cockpit-proj__lbl">{p('Δ masse fin / début')}</div>
                      <div className="hr-cockpit-proj__val" style={{ color: rampDelta < 0 ? 'var(--color-terracotta)' : 'var(--color-sage)' }}>
                        {`${rampDelta >= 0 ? '+' : ''}${m(rampDelta)}`}<small className="hr-kpi__unit"> {chfLbl}</small>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Cards 2 & 3 — Effectif & rotation | Variation salaires bruts ── */}
          <div className="hr-ov-row">
            <div className="hr-charges-card">
              <div className="hr-charges-card__band">EFFECTIF &amp; ROTATION · {ytdLabel}</div>
              <div className="hr-charges-card__body">
                {ytdNoPeriod ? (
                  <div className="hr-synth__note">ℹ {p('Aucun mois clos pour {{year}} — définir le dernier mois clôturé (Réglages).', { year })}</div>
                ) : (
                  <div className="hr-synth__grid hr-synth__grid--2">
                    <KpiCell label={t('phrases:p_3fb134ce5f')}   value={ytdCap ? formatNumber(ytdCap.eff_moyen, 1) : '—'} hint={p('moyenne sur les mois clos')} />
                    <KpiCell label={t('phrases:p_7a1ec63cd7')} value={ytdCap ? formatNumber(ytdCap.ept_moyen, 1) : '—'} hint={p('contrat × calendrier')} />
                    <KpiCell label={t('phrases:p_4da41c0fc5')}        value={ytdTurn ? `+${ytdTurn.entries} / −${ytdTurn.exits}` : '—'} hint={t('phrases:p_88bb38e51f')} />
                    <KpiCell label={p('Δ effectif net')}           value={ytdTurn ? `${ytdTurn.net >= 0 ? '+' : ''}${ytdTurn.net}` : '—'} hint={p('entrées − sorties')} accent={ytdTurn && ytdTurn.net < 0 ? 'terra' : 'sage'} />
                    <KpiCell label={p('Taux de rotation')}         value={ytdTurn ? `${formatNumber(ytdTurn.rate_pct, 1)}%` : '—'} hint={ytdTurn ? p('{{pct}}% annualisé', { pct: formatNumber(ytdTurn.rate_annualized_pct, 1) }) : '—'} />
                    <KpiCell label={t('phrases:p_21c92dab87')}     value={ytdCostPerFte != null ? m(ytdCostPerFte) : '—'} suffix={ytdCostPerFte != null ? chfLbl : undefined} hint={t('phrases:p_a190d6f369')} accent="primary" />
                  </div>
                )}
              </div>
            </div>

            <div className="hr-charges-card">
              <div className="hr-charges-card__band hr-charges-card__band--split">
                <span>{p('Salaires bruts {{year}}', { year })}</span>
                {hasExtraSalary ? (
                  <div
                    className="unit-switch hr-page__no-print" role="switch" aria-checked={smoothExtras}
                    aria-label={p('13e/14e salaire : vue cash ou lissé')} tabIndex={0}
                    onClick={() => setSmoothExtras(!smoothExtras)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSmoothExtras(!smoothExtras); } }}
                    style={{ textTransform: 'none', letterSpacing: 0 }}
                  >
                    <span className={`unit-switch__lab${!smoothExtras ? ' on' : ''}`}>{p('Vue cash')}</span>
                    <span className={`unit-switch__track${smoothExtras ? ' on' : ''}`}><span className="unit-switch__knob" /></span>
                    <span className={`unit-switch__lab${smoothExtras ? ' on' : ''}`}>{p('13e lissé')}</span>
                  </div>
                ) : (
                  <span style={{ color: 'var(--color-text-muted)', fontSize: 10, textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                    {p('Selon mois de paiement')}
                  </span>
                )}
              </div>
              <div className="hr-charges-card__body">
                <div className="hr-chart-total">{m(grossAnnual)}<small className="hr-kpi__unit"> {chfLbl}</small></div>
                <div className="hr-chart">
                  <div className="hr-chart__bars" style={{ gridTemplateColumns: 'repeat(12, 1fr)', gap: 6 }}>
                    {displayMonthly.map((v, i) => {
                      const h = Math.max(2, (v / maxBar) * 100);
                      const isProjected = (i + 1) > closedThrough;   // mois futur (non clos)
                      const fill = 'var(--color-accent)';            // réel = aplat ardoise
                      return (
                        <div className="hr-chart__col" key={i}>
                          <div className="hr-chart__val">{formatNumber(v / 1000, 0)}k</div>
                          <div className="hr-chart__bar-wrap">
                            <div
                              className="hr-chart__bar"
                              style={{
                                height: `${h}%`,
                                background: isProjected
                                  ? 'var(--color-accent-soft)'   // projeté = aplat ardoise clair
                                  : fill,
                              }}
                              title={`${MONTHS[i]} ${year} — ${formatNumber(v, 0)} CHF${isProjected ? p(' (projeté)') : ''}`}
                            />
                          </div>
                          <div className="hr-chart__month">{MONTHS[i]}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      )}
    </main>
  );
}

function KpiCell({ label, value, hint, accent, negative, suffix }) {
  return (
    <div className={`hr-kpi ${accent ? `hr-kpi--${accent}` : ''}`}>
      <div className="hr-kpi__label">{label}</div>
      <div className={`hr-kpi__value${negative ? ' hr-kpi__value--negative' : ''}`}>
        {value}{suffix && <small className="hr-kpi__unit"> {suffix}</small>}
      </div>
      {hint && <div className="hr-kpi__hint">{hint}</div>}
    </div>
  );
}
